import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type useQueryFilterType = {
  keys: Record<string, string | null | undefined>;
  updateParams: (key: string, value: string) => void;
  clearParams: () => void;
  searchParams: URLSearchParams;
  clearParam: (key: string) => void;
  getAllValue: (key: string) => string[] | null;
  appendSameKeys: (key: string, value: string) => void;
};

export const useQueryParams = (): useQueryFilterType => {
  const [params, setParams] = useSearchParams();
  const ignoreKeys = ["limit", "offset", "currentPage"];

  // params?.forEach((v, k) => console.log({ k, v }));

  // Function to delete a specific key
  const clearParam = useCallback(
    (key: string) => {
      if (ignoreKeys.includes(key)) {
        return;
      }
      params.delete(key); // Deletes the key-value pair from params
      setParams(params); // Updates the search parameters (triggers re-render with new params)
    },
    [params, setParams],
  );

  // Function to set a specific key-value pair
  const updateParams = useCallback(
    (key: string, value: string) => {
      if (ignoreKeys.includes(key)) {
        return;
      } else {
        // Otherwise, use set() to update or replace the key-value pair
        params.set(key, value); // If the key exists, replace it, otherwise add it
      }
      setParams(params); // Updates the search parameters
    },
    [params, setParams],
  );

  const appendSameKeys = useCallback(
    (key: string, value: string) => {
      if (ignoreKeys.includes(key)) {
        return;
      } else {
        params.append(key, value);
      }
      setParams(params); // Update the search parameters
    },
    [params, setParams],
  );

  // Function to delete all keys
  const clearParams = useCallback(() => {
    const newParams = new URLSearchParams(params); // Create a copy

    // Delete all keys except ignored ones
    Array.from(newParams.keys()).forEach((key) => {
      if (!ignoreKeys.includes(key)) {
        newParams.delete(key);
      }
    });

    setParams(newParams); // Update with the modified copy
  }, [params, setParams]);

  const getAllValue = useCallback(
    (key: string) => {
      return params.getAll(key);
    },
    [params, setParams],
  );

  // Use useMemo to convert keys to a Record<string, string | null | undefined>
  const keys = useMemo(() => {
    const result: Record<string, string | null | undefined> = {};
    params.forEach((value, key) => {
      if (!ignoreKeys.includes(key)) {
        result[key] = value ?? null; // Assign the value, or null if undefined
      }
    });

    return result;
  }, [params]);

  return {
    keys,
    clearParam,
    clearParams,
    searchParams: params,
    updateParams,
    getAllValue,
    appendSameKeys,
  };
};

export default useQueryParams;
